import type { ButtonHTMLAttributes, ReactNode } from "react";

type IconName =
  | "arrow-left"
  | "arrow-right"
  | "check"
  | "coins"
  | "close"
  | "download"
  | "image"
  | "lock"
  | "person"
  | "spark"
  | "works";

const paths: Record<IconName, ReactNode> = {
  "arrow-left": <path d="m15 18-6-6 6-6" />,
  "arrow-right": <path d="m9 18 6-6-6-6" />,
  check: <path d="m5 12 4 4L19 6" />,
  coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v5c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 11v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /></>,
  close: <><path d="m6 6 12 12" /><path d="M18 6 6 18" /></>,
  download: <><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></>,
  image: <><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m5 18 5-5 3 3 2-2 4 4" /></>,
  lock: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  person: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  spark: <path d="m12 2 2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2L12 2Z" />,
  works: <><rect x="3" y="4" width="18" height="16" rx="3" /><path d="m6 17 4-4 3 3 2-2 3 3" /></>,
};

export function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      className="icon"
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
    >
      <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8">
        {paths[name]}
      </g>
    </svg>
  );
}

export function Brand({ compact = false }: { compact?: boolean }) {
  const darkAsset = compact ? "/nura-mark-dark.png" : "/nura-logo-header-144px.png";
  const lightAsset = compact ? "/nura-mark-light.png" : "/nura-logo-light.png";
  return (
    <div className={compact ? "brand brand--compact" : "brand"} aria-label="Nura">
      <img className="brand__dark" src={darkAsset} alt="Nura" />
      <img className="brand__light" src={lightAsset} alt="Nura" />
    </div>
  );
}

export function BackButton({ label, onClick }: { label: string; onClick(): void }) {
  return (
    <button className="icon-button" type="button" aria-label={label} onClick={onClick}>
      <Icon name="arrow-left" />
    </button>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  tail?: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
};

export function Button({ children, tail, variant = "primary", className = "", ...props }: ButtonProps) {
  return (
    <button className={`button button--${variant} ${className}`.trim()} type="button" {...props}>
      <span>{children}</span>
      {tail !== undefined ? <span className="button__tail">{tail}</span> : null}
    </button>
  );
}

export function BalanceButton({ balance, onClick }: { balance?: number; onClick?(): void }) {
  return (
    <button className="balance" type="button" onClick={onClick} aria-label={`Баланс: ${balance ?? "загружается"}`}>
      <Icon name="coins" size={21} />
      <span>{balance === undefined ? "—" : balance} {balance === 1 ? "кредит" : balance !== undefined && balance < 5 ? "кредита" : "кредитов"}</span>
    </button>
  );
}

export function OrnamentDivider() {
  return <div className="ornament-divider" aria-hidden="true"><span /><i /><span /></div>;
}

export function ScreenHeader({
  title,
  onBack,
  balance,
  onOpenBalance,
}: {
  title: string;
  onBack(): void;
  balance?: number;
  onOpenBalance?(): void;
}) {
  return (
    <header className="screen-header">
      <BackButton label="Вернуться назад" onClick={onBack} />
      <span className="screen-header__title">{title}</span>
      {onOpenBalance !== undefined ? <BalanceButton balance={balance} onClick={onOpenBalance} /> : <span className="screen-header__spacer" />}
    </header>
  );
}
